import { createSvgEl } from '@learnforge/demo-render';

/** Plane in standard form a·x + b·y + c·z = d. */
export interface Plane3 {
  a: number;
  b: number;
  c: number;
  d: number;
}

const EPS = 1e-9;

/** Camera orientation: yaw around the vertical axis, pitch tilting it down. */
export interface View3D {
  yaw: number; // radians
  pitch: number; // radians, clamped to [0.1, 1.4]
}

export const DEFAULT_VIEW: View3D = { yaw: Math.PI / 4, pitch: Math.PI / 6 };

/**
 * Pure orthographic projection: rotate (x, y) by yaw around the z axis, then
 * tilt the rotated y toward the viewer by pitch and fold z in as screen-up.
 * Unscaled — createSystem3D fits the result to its box.
 */
export function project(x: number, y: number, z: number, view: View3D): { px: number; py: number } {
  const xr = x * Math.cos(view.yaw) - y * Math.sin(view.yaw);
  const yr = x * Math.sin(view.yaw) + y * Math.cos(view.yaw);
  return { px: xr, py: yr * Math.sin(view.pitch) - z * Math.cos(view.pitch) };
}

type Vec3 = [number, number, number];

const CORNERS = (h: number): Vec3[] => [
  [-h, -h, -h],
  [h, -h, -h],
  [-h, h, -h],
  [h, h, -h],
  [-h, -h, h],
  [h, -h, h],
  [-h, h, h],
  [h, h, h],
];

// Pairs of corner indices forming the 12 cube edges.
const EDGES: [number, number][] = [
  [0, 1],
  [0, 2],
  [1, 3],
  [2, 3],
  [4, 5],
  [4, 6],
  [5, 7],
  [6, 7],
  [0, 4],
  [1, 5],
  [2, 6],
  [3, 7],
];

/**
 * The polygon where a plane crosses the cube [−half, half]³: intersect the
 * plane with each cube edge, dedupe, then order the hits around their
 * centroid in the plane's own 2D basis. Empty if fewer than 3 hits.
 */
export function planeCubeIntersection(plane: Plane3, half: number): Vec3[] {
  const { a, b, c, d } = plane;
  const corners = CORNERS(half);
  const f = (p: Vec3): number => a * p[0] + b * p[1] + c * p[2] - d;

  const hits: Vec3[] = [];
  const addHit = (p: Vec3): void => {
    if (
      !hits.some(
        (q) =>
          Math.abs(q[0] - p[0]) < 1e-6 &&
          Math.abs(q[1] - p[1]) < 1e-6 &&
          Math.abs(q[2] - p[2]) < 1e-6,
      )
    ) {
      hits.push(p);
    }
  };

  for (const [i, j] of EDGES) {
    const p0 = corners[i]!;
    const p1 = corners[j]!;
    const f0 = f(p0);
    const f1 = f(p1);
    if (Math.abs(f0) < EPS) addHit(p0);
    if (Math.abs(f1) < EPS) addHit(p1);
    if (f0 * f1 < -EPS) {
      const t = f0 / (f0 - f1);
      addHit([
        p0[0] + t * (p1[0] - p0[0]),
        p0[1] + t * (p1[1] - p0[1]),
        p0[2] + t * (p1[2] - p0[2]),
      ]);
    }
  }

  if (hits.length < 3) return [];

  // Order around the centroid using an orthonormal basis (u, v) of the plane.
  const n: Vec3 = [a, b, c];
  const ref: Vec3 = Math.abs(c) < 0.9 * Math.hypot(a, b, c) ? [0, 0, 1] : [1, 0, 0];
  const cross = (p: Vec3, q: Vec3): Vec3 => [
    p[1] * q[2] - p[2] * q[1],
    p[2] * q[0] - p[0] * q[2],
    p[0] * q[1] - p[1] * q[0],
  ];
  const norm = (p: Vec3): Vec3 => {
    const len = Math.hypot(p[0], p[1], p[2]);
    return [p[0] / len, p[1] / len, p[2] / len];
  };
  const u = norm(cross(n, ref));
  const v = norm(cross(n, u));
  const centroid: Vec3 = [
    hits.reduce((s, p) => s + p[0], 0) / hits.length,
    hits.reduce((s, p) => s + p[1], 0) / hits.length,
    hits.reduce((s, p) => s + p[2], 0) / hits.length,
  ];
  const dot = (p: Vec3, q: Vec3): number => p[0] * q[0] + p[1] * q[1] + p[2] * q[2];
  const angle = (p: Vec3): number => {
    const rel: Vec3 = [p[0] - centroid[0], p[1] - centroid[1], p[2] - centroid[2]];
    return Math.atan2(dot(rel, v), dot(rel, u));
  };
  return [...hits].sort((p, q) => angle(p) - angle(q));
}

export interface System3DOptions {
  planes: Plane3[];
  point: [number, number, number] | null;
  half: number;
  width: number;
  height: number;
  view?: View3D; // defaults to DEFAULT_VIEW
  onViewChange?: (view: View3D) => void;
}

export interface System3DHandle {
  group: SVGGElement;
  setView(view: View3D): void; // re-renders
  destroy(): void;
}

const MARGIN = 14;
const MIN_PITCH = 0.1;
const MAX_PITCH = 1.4;

/**
 * Rotatable orthographic view: cube wireframe, x/y/z axes, one translucent
 * polygon per plane, and the unique-solution point when inside the cube.
 * Drag on the stage to orbit the camera; the view is re-fit to the box on
 * every render since the projection changes with it.
 */
export function createSystem3D(
  parent: SVGSVGElement | SVGGElement,
  opts: System3DOptions,
): System3DHandle {
  const group = createSvgEl('g', { class: 'alg-3d-stage' });
  parent.appendChild(group);

  const { planes, point, half, width, height } = opts;
  let view: View3D = { ...(opts.view ?? DEFAULT_VIEW) };

  const render = (): void => {
    while (group.firstChild) {
      group.removeChild(group.firstChild);
    }

    // Fit: project the cube corners, scale into width×height with a margin.
    const projected = CORNERS(half).map((p) => project(p[0], p[1], p[2], view));
    const minX = Math.min(...projected.map((p) => p.px));
    const maxX = Math.max(...projected.map((p) => p.px));
    const minY = Math.min(...projected.map((p) => p.py));
    const maxY = Math.max(...projected.map((p) => p.py));
    const scale = Math.min(
      (width - 2 * MARGIN) / (maxX - minX),
      (height - 2 * MARGIN) / (maxY - minY),
    );
    const offX = (width - (maxX - minX) * scale) / 2;
    const offY = (height - (maxY - minY) * scale) / 2;
    const toScreen = (x: number, y: number, z: number): { px: number; py: number } => {
      const p = project(x, y, z, view);
      return {
        px: offX + (p.px - minX) * scale,
        py: offY + (p.py - minY) * scale,
      };
    };

    // Cube wireframe
    const corners = CORNERS(half);
    for (const [i, j] of EDGES) {
      const p0 = toScreen(...corners[i]!);
      const p1 = toScreen(...corners[j]!);
      group.appendChild(
        createSvgEl('line', { x1: p0.px, y1: p0.py, x2: p1.px, y2: p1.py, class: 'alg-gridline' }),
      );
    }

    // Axes through the origin, with labels just past the +ve ends
    const axes: { to: Vec3; label: string }[] = [
      { to: [half, 0, 0], label: 'x' },
      { to: [0, half, 0], label: 'y' },
      { to: [0, 0, half], label: 'z' },
    ];
    for (const { to, label } of axes) {
      const p0 = toScreen(-to[0], -to[1], -to[2]);
      const p1 = toScreen(to[0], to[1], to[2]);
      group.appendChild(
        createSvgEl('line', {
          x1: p0.px,
          y1: p0.py,
          x2: p1.px,
          y2: p1.py,
          class: 'alg-sketch-axis',
        }),
      );
      const lp = toScreen(to[0] * 1.08, to[1] * 1.08, to[2] * 1.08);
      const text = createSvgEl('text', {
        x: lp.px,
        y: lp.py,
        'text-anchor': 'middle',
        class: 'alg-text-mute',
      });
      text.textContent = label;
      group.appendChild(text);
    }

    // Planes
    planes.forEach((plane, i) => {
      const poly = planeCubeIntersection(plane, half);
      if (poly.length < 3) return;
      const pts = poly
        .map((p) => {
          const s = toScreen(p[0], p[1], p[2]);
          return `${s.px},${s.py}`;
        })
        .join(' ');
      group.appendChild(createSvgEl('polygon', { points: pts, class: `alg-plane-${i + 1}` }));
    });

    // Unique-solution point (only when inside the display cube)
    if (
      point !== null &&
      Math.abs(point[0]) <= half &&
      Math.abs(point[1]) <= half &&
      Math.abs(point[2]) <= half
    ) {
      const s = toScreen(point[0], point[1], point[2]);
      group.appendChild(
        createSvgEl('circle', {
          cx: s.px,
          cy: s.py,
          r: 11,
          class: 'alg-point',
          'fill-opacity': '0.2',
        }),
      );
      group.appendChild(createSvgEl('circle', { cx: s.px, cy: s.py, r: 5, class: 'alg-point' }));
    }
  };

  render();

  // Drag-to-rotate
  let dragging = false;
  let last = { x: 0, y: 0 };

  const onPointerDown = (e: Event): void => {
    const pe = e as PointerEvent;
    dragging = true;
    last = { x: pe.clientX, y: pe.clientY };
    // Capture on the persistent group, not e.target: render() clears and
    // rebuilds child shapes on every pointermove, which would otherwise
    // implicitly release capture held by a since-removed child.
    if (typeof group.setPointerCapture === 'function') {
      try {
        group.setPointerCapture(pe.pointerId);
      } catch {
        /* jsdom */
      }
    }
  };

  const onPointerMove = (e: Event): void => {
    if (!dragging) return;
    const pe = e as PointerEvent;
    const dx = pe.clientX - last.x;
    const dy = pe.clientY - last.y;
    last = { x: pe.clientX, y: pe.clientY };
    view.yaw += dx * 0.01;
    view.pitch = Math.min(MAX_PITCH, Math.max(MIN_PITCH, view.pitch - dy * 0.01));
    render();
    opts.onViewChange?.({ ...view });
  };

  const onPointerUp = (e: Event): void => {
    dragging = false;
    const pe = e as PointerEvent;
    if (typeof group.releasePointerCapture === 'function') {
      try {
        group.releasePointerCapture(pe.pointerId);
      } catch {
        /* jsdom */
      }
    }
  };

  group.addEventListener('pointerdown', onPointerDown);
  group.addEventListener('pointermove', onPointerMove);
  group.addEventListener('pointerup', onPointerUp);
  group.addEventListener('pointercancel', onPointerUp);

  return {
    group,
    setView(v: View3D): void {
      view = { ...v, pitch: Math.min(MAX_PITCH, Math.max(MIN_PITCH, v.pitch)) };
      render();
    },
    destroy(): void {
      if (group.parentNode) {
        group.parentNode.removeChild(group);
      }
    },
  };
}
